import { useRef, useState } from "react";
import { Filter, FilterX, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { MappingFilterColumn } from "./mappingFilters";

interface Props {
  column: MappingFilterColumn;
  availableValues: string[];
  selected?: ReadonlySet<string>;
  onApply: (values: ReadonlySet<string> | null) => void;
}

const menuItemClass = "flex h-7 w-full items-center gap-3 px-3 text-left hover:bg-[#e6f0e9] focus-visible:bg-[#e6f0e9] focus-visible:shadow-none focus-visible:outline-none disabled:text-[#999] disabled:hover:bg-transparent";
const checkboxClass = "h-[13px] w-[13px] shrink-0 accent-[#217346] focus-visible:shadow-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[#217346]";

export function MappingColumnFilter({ column, availableValues, selected, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<ReadonlySet<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const matches = availableValues.filter(value => (value || "(Blanks)").toLowerCase().includes(search.trim().toLowerCase()));
  const matchingSelection = matches.filter(value => draft.has(value));
  const allMatching = matches.length > 0 && matchingSelection.length === matches.length;
  const partlyMatching = matchingSelection.length > 0 && !allMatching;

  const apply = () => {
    if (matchingSelection.length === 0) return;
    const values = new Set(matchingSelection);
    onApply(values.size === column.values.length ? null : values);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={next => {
      if (next) {
        setSearch("");
        setDraft(new Set(selected ?? availableValues));
      }
      setOpen(next);
    }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`Filter ${column.label}`} aria-pressed={Boolean(selected)}
          className={`absolute bottom-px right-px flex h-[17px] w-[19px] items-center justify-center border border-[#a6a6a6] bg-[#f2f2f2] text-[#333] hover:border-[#217346] hover:bg-[#dcece2] ${open ? "border-[#217346] bg-[#dcece2]" : ""}`}
          onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
          {selected ? <Filter size={11} aria-hidden="true" /> : <span aria-hidden="true" className="h-0 w-0 border-x-[3px] border-t-[4px] border-x-transparent border-t-current" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={1}
        className="flex h-[306px] max-h-[var(--radix-popover-content-available-height)] w-[264px] min-w-[240px] max-w-[calc(100vw-16px)] resize flex-col overflow-hidden rounded-none border-[#ababab] bg-white p-0 text-[12px] font-normal text-[#222] shadow-[2px_3px_9px_rgba(0,0,0,0.22)] !animate-none"
        style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }}
        aria-label={`Filter ${column.label}`} onPointerDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
        onOpenAutoFocus={event => { event.preventDefault(); searchRef.current?.focus(); }}>
        <div className="shrink-0 border-b border-[#ddd] py-1">
          <button type="button" className={menuItemClass} disabled={!selected}
            onClick={() => { onApply(null); setOpen(false); }}>
            <FilterX size={16} strokeWidth={1.5} className="shrink-0" aria-hidden="true" />
            <span className="truncate">Clear Filter From “{column.label}”</span>
          </button>
        </div>
        <form className="flex min-h-0 flex-1 flex-col px-2 pt-2" onSubmit={event => { event.preventDefault(); apply(); }}>
          <div className="relative mb-2 shrink-0">
            <input ref={searchRef} aria-label={`Search ${column.label}`} placeholder="Search" value={search}
              onChange={event => setSearch(event.target.value)}
              className="h-6 w-full rounded-none border border-[#b7b7b7] bg-white pl-1.5 pr-6 text-[12px] outline-none placeholder:text-[#777] focus:border-[#217346] focus-visible:shadow-none" />
            <Search size={13} className="pointer-events-none absolute right-1.5 top-1.5 text-[#777]" aria-hidden="true" />
          </div>
          <div className="min-h-12 flex-1 overflow-auto border border-[#c6c6c6] bg-white py-1">
            {matches.length === 0 ? <p className="px-3 py-1 text-[#777]">No matches found.</p> : <>
              <label className="flex h-5 cursor-pointer items-center gap-1.5 whitespace-nowrap px-3 hover:bg-[#e6f0e9]">
                <input ref={element => { if (element) element.indeterminate = partlyMatching; }} type="checkbox" checked={allMatching}
                  onChange={() => setDraft(current => {
                    const next = new Set(current);
                    matches.forEach(value => allMatching ? next.delete(value) : next.add(value));
                    return next;
                  })} className={checkboxClass} />
                (Select All)
              </label>
              {matches.map(value => (
                <label key={value} className="flex h-5 min-w-max cursor-pointer items-center gap-1.5 whitespace-nowrap pl-6 pr-3 hover:bg-[#e6f0e9]">
                  <input type="checkbox" checked={draft.has(value)} className={checkboxClass}
                    onChange={() => setDraft(current => {
                      const next = new Set(current);
                      if (next.has(value)) next.delete(value); else next.add(value);
                      return next;
                    })} />
                  <span>{value || "(Blanks)"}</span>
                </label>
              ))}
            </>}
          </div>
          <div className="flex shrink-0 justify-end gap-2 py-2.5">
            <button type="submit" disabled={matchingSelection.length === 0}
              className="h-6 min-w-[68px] border border-[#217346] bg-[#217346] px-3 text-white hover:bg-[#185c37] disabled:border-[#ccc] disabled:bg-[#eee] disabled:text-[#999]">OK</button>
            <button type="button" onClick={() => setOpen(false)}
              className="h-6 min-w-[68px] border border-[#aaa] bg-[#f3f3f3] px-3 hover:bg-[#e5e5e5]">Cancel</button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
